import { useEffect, useState } from 'react'

export interface PublicProject {
  id: number
  title: string
  slug?: string
  category: string
  location: string
  year: string
  client: string
  image_url: string
  gallery_images: string[]
  description: string
  services: string[]
  status: string
}

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string')
  if (typeof value !== 'string') return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : []
  } catch {
    return []
  }
}

function normalizeProject(project: Partial<PublicProject> & Record<string, unknown>): PublicProject {
  const galleryImages = asStringArray(project.gallery_images ?? project.images)
  return {
    id: Number(project.id), title: String(project.title || ''), slug: typeof project.slug === 'string' ? project.slug : undefined,
    category: String(project.category || 'Project'), location: String(project.location || ''), year: String(project.year || ''),
    client: String(project.client || ''), image_url: String(project.image_url || galleryImages[0] || ''), gallery_images: galleryImages,
    description: String(project.description || ''), services: asStringArray(project.services), status: String(project.status || 'completed'),
  }
}

export function useProjects() {
  const [projects, setProjects] = useState<PublicProject[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    async function loadProjects() {
      try {
        const response = await fetch('/api/projects', { signal: controller.signal })
        if (!response.ok) throw new Error('Unable to load projects')
        const data: unknown = await response.json()
        if (!Array.isArray(data)) throw new Error('Invalid projects response')
        setProjects(data.map((project) => normalizeProject(project as Record<string, unknown>)))
      } catch (loadError) {
        if ((loadError as Error).name !== 'AbortError') setError('Projects are unavailable right now. Please try again shortly.')
      } finally {
        if (!controller.signal.aborted) setIsLoading(false)
      }
    }
    loadProjects()
    return () => controller.abort()
  }, [])

  return { projects, isLoading, error }
}
